import { Col, ErrorText, Hint, Row } from '@elotech/components';
import classnames from 'classnames';
import React from 'react';
import NumberFormat from 'react-number-format';

import { useCamposLocalizacao } from '../../../hooks/useCamposLocalizacao';
import { CadastroRural } from '../../../type';
import {
  CampoLocalizacao,
  getCamposLocalizacaoRural
} from '../../../utils/InscricaoCadastralUtils';

type Props = {
  cadastro?: CadastroRural;
  onChangeCadastro: (event: React.ChangeEvent<any>) => void;
  handleBlurCadastroRural: (event: React.ChangeEvent<any>) => void;
  error: Record<string, boolean>;
  errorMessages: Record<string, string>;
  loading: boolean;
  notFound: boolean;
  permiteCriacaoCadastroRuralItbi?: boolean;
  /** Campos de localização já carregados pela tela; sem eles, o componente carrega a configuração. */
  camposLocalizacao?: CampoLocalizacao[];
};

const LocalizacaoImovelRural: React.FC<Props> = ({
  cadastro,
  onChangeCadastro,
  loading,
  handleBlurCadastroRural,
  notFound,
  error,
  errorMessages,
  permiteCriacaoCadastroRuralItbi,
  camposLocalizacao
}) => {
  const camposRurais = getCamposLocalizacaoRural(
    useCamposLocalizacao(camposLocalizacao)
  );

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleBlurCadastroRural(event);
    }
  };

  const onChangeValorDecimal = (
    { floatValue }: { floatValue?: number },
    event: React.ChangeEvent<any>
  ) => {
    onChangeCadastro({
      target: { name: event.target.name, value: floatValue }
    } as React.ChangeEvent<any>);
  };

  const camposDescritivosEditaveis =
    !!permiteCriacaoCadastroRuralItbi && !cadastro?.cadastro;

  const inputCadastroClassName = classnames({
    loading,
    error: notFound || error.cadastro || error.cadastroInadimplente
  });
  return (
    <>
      <Row>
        <Col md={3} className="form-group">
          <label htmlFor="cadastroRural">Cadastro Rural</label>
          <NumberFormat
            id="cadastroRural"
            name="cadastro"
            value={cadastro?.cadastro}
            onChange={onChangeCadastro}
            onKeyDown={onKeyDown}
            onBlur={handleBlurCadastroRural}
            maxLength={10}
            decimalScale={0}
            allowNegative={false}
            placeholder="Digite o código do cadastro"
            autoComplete="off"
            className={inputCadastroClassName}
          />
          {loading && <i className="fa fa-spinner spinner" />}
          {notFound && (
            <div>
              <ErrorText>
                {permiteCriacaoCadastroRuralItbi
                  ? 'Cadastro rural não encontrado. Informe os dados do imóvel abaixo para prosseguir com a declaração.'
                  : 'Não foi possível localizar o cadastro rural!'}
              </ErrorText>
            </div>
          )}
          {!notFound && error.cadastro && (
            <ErrorText>{errorMessages.cadastroRural}</ErrorText>
          )}
          {error.cadastroInadimplente && (
            <ErrorText>{errorMessages.cadastroInadimplente}</ErrorText>
          )}
        </Col>

        <Col className="form-group" md={2}>
          <label htmlFor="matriculaInput">Matrícula</label>
          <input
            id="matriculaInput"
            type="text"
            name="matricula"
            value={cadastro?.matricula || ''}
            placeholder={'Digite a matrícula'}
            maxLength={200}
            onChange={onChangeCadastro}
            onBlur={handleBlurCadastroRural}
            onKeyDown={onKeyDown}
            className={loading ? 'loading' : error.matricula ? 'error' : ''}
          />
          {loading && <i className="fa fa-spinner spinner" />}
          {error.matricula && <ErrorText>{errorMessages.matricula}</ErrorText>}
        </Col>
        <Col md={4} className={'form-group'}>
          <label htmlFor="nome">Nome da Propriedade</label>
          <input
            id="nome"
            type="text"
            name="nome"
            value={cadastro?.nome ?? ''}
            onChange={onChangeCadastro}
            maxLength={200}
            disabled={!camposDescritivosEditaveis}
          />
        </Col>
        <Col md={5} className={'form-group'}>
          <label htmlFor="enderecoDescrito">
            Endereço
            <Hint
              classes={`inline clean module-color fa-exclamation-circle mobile sm`}
            >
              Informe o endereço por extenso referente ao cadastro rural
            </Hint>
          </label>
          <input
            id="enderecoDescrito"
            type="text"
            name="enderecoDescrito"
            value={cadastro?.enderecoDescrito ?? ''}
            onChange={onChangeCadastro}
            maxLength={250}
            disabled={!camposDescritivosEditaveis}
            className={error.enderecoDescrito ? 'error' : ''}
          />
          {error.enderecoDescrito && (
            <ErrorText>{errorMessages.enderecoDescrito}</ErrorText>
          )}
        </Col>
      </Row>
      <Row>
        <Col md={4} className={'form-group'}>
          <label htmlFor="logradouro">Logradouro</label>
          <input
            id="logradouro"
            type="text"
            name="logradouro"
            value={cadastro?.logradouro ?? ''}
            disabled
          />
        </Col>
        <Col md={2} className={'form-group'}>
          <label htmlFor="bairro">Bairro</label>
          <input
            id="bairro"
            type="text"
            name="bairro"
            value={cadastro?.bairro ?? ''}
            disabled
          />
        </Col>
        <Col md={2} className={'form-group'}>
          <label htmlFor="complemento">Complemento</label>
          <input
            id="complemento"
            type="text"
            name="complemento"
            value={cadastro?.complemento ?? ''}
            disabled
          />
        </Col>
        {camposRurais.map(({ campo, label, campoCadastro }) => (
          <Col md={2} className={'form-group'} key={campo}>
            <label htmlFor={campoCadastro}>{label}</label>
            <input
              id={campoCadastro}
              type="text"
              name={campoCadastro}
              value={cadastro?.[campoCadastro as keyof CadastroRural] ?? ''}
              disabled
            />
          </Col>
        ))}
      </Row>
      <Row>
        <Col md={2} className={'form-group'}>
          <label htmlFor="numeroReceita">
            NIRF
            <Hint
              classes={`inline clean module-color fa-exclamation-circle mobile sm`}
            >
              Número do Imóvel na Receita Federal
            </Hint>
          </label>
          <input
            id="numeroReceita"
            type="text"
            name="numeroReceita"
            value={cadastro?.numeroReceita ?? ''}
            onChange={onChangeCadastro}
            maxLength={20}
            disabled={!camposDescritivosEditaveis}
          />
        </Col>
        <Col md={2} className={'form-group'}>
          <label htmlFor="numeroIncra">Número no INCRA</label>
          <input
            id="numeroIncra"
            type="text"
            name="numeroIncra"
            value={cadastro?.numeroIncra ?? ''}
            onChange={onChangeCadastro}
            maxLength={20}
            disabled={!camposDescritivosEditaveis}
          />
        </Col>
        <Col md={2} className={'form-group'}>
          <label htmlFor="alqueires">Qtd. Alqueires</label>
          <NumberFormat
            id="alqueires"
            name="alqueires"
            value={cadastro?.alqueires ?? ''}
            onValueChange={onChangeValorDecimal}
            decimalScale={2}
            fixedDecimalScale
            decimalSeparator=","
            thousandSeparator="."
            allowNegative={false}
            placeholder="0,00"
            isAllowed={({ value }) => value.length <= 11}
            disabled={!camposDescritivosEditaveis}
          />
        </Col>
        <Col md={2} className={'form-group'}>
          <label htmlFor="hectares">Qtd. Hectares</label>
          <NumberFormat
            id="hectares"
            name="hectares"
            value={cadastro?.hectares ?? ''}
            onValueChange={onChangeValorDecimal}
            decimalScale={2}
            fixedDecimalScale
            decimalSeparator=","
            thousandSeparator="."
            allowNegative={false}
            placeholder="0,00"
            isAllowed={({ value }) => value.length <= 11}
            disabled={!camposDescritivosEditaveis}
          />
        </Col>
        <Col md={2} className={'form-group'}>
          <label htmlFor="areaTotal">Área total</label>
          <NumberFormat
            id="areaTotal"
            name="areaTotal"
            value={cadastro?.areaTotal ?? ''}
            onValueChange={onChangeValorDecimal}
            decimalScale={2}
            fixedDecimalScale
            decimalSeparator=","
            thousandSeparator="."
            allowNegative={false}
            placeholder="0,00"
            isAllowed={({ value }) => value.length <= 11}
            disabled={!camposDescritivosEditaveis}
          />
        </Col>
      </Row>
    </>
  );
};

export default LocalizacaoImovelRural;
